<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { mdiDomain, mdiMapMarkerRadiusOutline, mdiOfficeBuildingOutline } from '@mdi/js';
import { useEstateStore } from '../stores/estate.ts';
import { ancestry } from '../lib/tree.ts';

/**
 * The estate as a tree in the navigation.
 *
 * Selecting a node opens that twin's dashboard, and the route decides which
 * node is highlighted and which branches are open, never the other way round.
 * So a link shared by a colleague, a back button or a reload opens the tree at
 * the right place, which is chapter 17's URL-as-state carried into navigation.
 */
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const estate = useEstateStore();

const current = computed(() => (route.name === 'twin' ? String(route.params.id) : estate.focus));
const opened = ref<string[]>([]);

// Open every ancestor of the current twin, keeping whatever else the operator
// opened by hand.
watch(
  [current, () => estate.twins],
  ([id]) => {
    if (!id) return;
    const path = ancestry(estate.twins, id).slice(0, -1);
    opened.value = [...new Set([...opened.value, ...path])];
  },
  { immediate: true },
);

function select(ids: unknown): void {
  const [id] = ids as string[];
  if (id && id !== current.value) void router.push({ name: 'twin', params: { id } });
}

/** Depth decides the icon: the estate root, a district, a building. */
function iconFor(depth: number): string {
  return depth === 0
    ? mdiDomain
    : depth === 1
      ? mdiMapMarkerRadiusOutline
      : mdiOfficeBuildingOutline;
}
const depthOf = (id: string) => ancestry(estate.twins, id).length - 1;
</script>

<template>
  <div>
    <v-skeleton-loader v-if="!estate.loaded" type="list-item@4" class="bg-transparent" />
    <p v-else-if="estate.twins.length === 0" class="px-4 text-body-small text-medium-emphasis">
      {{ t('tree.empty') }}
    </p>
    <v-treeview
      v-else
      v-model:opened="opened"
      :activated="current ? [current] : []"
      :items="estate.tree"
      item-value="id"
      item-title="title"
      item-children="children"
      activatable
      density="compact"
      :aria-label="t('nav.sites')"
      @update:activated="select"
    >
      <template #prepend="{ item }">
        <v-icon :icon="iconFor(depthOf(item.id))" size="small" />
      </template>
      <template #append="{ item }">
        <span v-if="item.deviceTotal" class="text-label-small text-medium-emphasis tabular">
          {{ item.deviceTotal }}
        </span>
      </template>
    </v-treeview>
  </div>
</template>
